// Cheese Club month selector: accessible tabs that reveal each month's cheeses,
// auto-selecting the tab matching the current calendar month where one exists.
class CheeseClubMonths extends HTMLElement {
  /** @type {HTMLElement[]} */
  tabs = [];

  /** @type {HTMLElement[]} */
  panels = [];

  /** @type {AbortController | null} */
  abortController = null;

  connectedCallback() {
    this.tabs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
    this.panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tabpanel"]')));
    if (this.tabs.length === 0) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab), { signal });
      tab.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    });

    this.selectCurrentMonth();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  /**
   * @param {HTMLElement} tab
   * @param {{ focus?: boolean }} [options]
   */
  activate(tab, { focus = false } = {}) {
    this.tabs.forEach((current) => {
      const isSelected = current === tab;
      current.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      current.tabIndex = isSelected ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('aria-labelledby') !== tab.id;
    });
    if (focus) tab.focus();
  }

  selectCurrentMonth() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear());

    const exactMatch = this.tabs.find((tab) => tab.dataset.month === month && tab.dataset.year === year);
    const monthMatch = this.tabs.find((tab) => tab.dataset.month === month);
    const match = exactMatch || monthMatch;

    // Falls back to the first tab (already selected server-side) when no month matches.
    if (match) this.activate(match);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const currentIndex = this.tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    if (currentIndex === -1) return;

    /** @type {HTMLElement | null | undefined} */
    let nextTab = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextTab = this.tabs[(currentIndex + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextTab = this.tabs[(currentIndex - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        nextTab = this.tabs[0];
        break;
      case 'End':
        nextTab = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    if (nextTab) this.activate(nextTab, { focus: true });
  }
}

if (!customElements.get('cheese-club-months')) {
  customElements.define('cheese-club-months', CheeseClubMonths);
}
